import {
	CONTACT_DIRECTORY_SCOPE,
	QUERY_KEYS,
	defaultMetaData,
	emptyMetaData,
} from "@/libraries/constant";
import { getIsoDateTime } from "@/libraries/date";
import { unix } from "@/libraries/dayjs-utc";
import { fileToString, isFiles, randInt } from "@/libraries/helper";
import { applyDataChanges } from "@/libraries/transform";
import type {
	CreateGroupSchema,
	GetTeamsParamsSchema,
	SyncGroupParamsSchema,
	UpdateGroupSchema,
} from "@/schemas";
import { groupService } from "@/services";
import { previewService } from "@/services/preview";
import { useSyncStore } from "@/stores/sync";
import type {
	GetGroupResponse,
	GetGroupsResponse,
	GetTeamsResponse,
	Group,
	MemberProfileResponse,
} from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { pick } from "lodash";
import type { PartialDeep } from "type-fest";

export const groupMutations = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_GROUP_CREATE], {
		mutationFn: (payload: CreateGroupSchema) => groupService.create(payload),
		retry: 2,
		onMutate: async (newGroup) => {
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_GROUPS],
			});
			const avatar =
				newGroup.avatar && isFiles(newGroup.avatar)
					? fileToString(newGroup.avatar).at(0)
					: null;
			const optimisticGroup: PartialDeep<Group> = {
				id: randInt(),
				avatar: avatar || null,
				name: newGroup.name,
				organization: newGroup.organization || "-",
				synopsis: newGroup.synopsis || "-",
				location: newGroup.location || "-",
				directory: newGroup.directory || false,
				type: "group",
				unread: 0,
				created_at: getIsoDateTime(),
				conversation_updated_at: getIsoDateTime(),
			};
			if (currentUser?.user) {
				optimisticGroup.user = {
					id: currentUser.user.id,
					email: currentUser.user.email,
					username: currentUser.user.username,
					first_name: currentUser.user.first_name || "-",
					last_name: currentUser.user.last_name || "-",
					registration_number: currentUser.user.registration_number || "-",
					phone_number: currentUser.user.phone_number || "-",
					nick_name: currentUser.user.nick_name || "-",
					qualification: currentUser.user.qualification || "-",
					spesialization: currentUser.user.spesialization || "-",
					job_title: currentUser.user.job_title || "-",
					title: currentUser.user.title || "-",
					created_at: currentUser.user.created_at,
					updated_at: currentUser.user.updated_at,
					hospital: currentUser.user.hospital ?? null,
				};
				optimisticGroup.members = [
					{
						id: currentUser.user.id,
						email: currentUser.user.email,
						username: currentUser.user.username,
						first_name: currentUser.user.first_name || "-",
						last_name: currentUser.user.last_name || "-",
						fullname: currentUser.user.fullname || "-",
						registration_number: currentUser.user.registration_number || "-",
						phone_number: currentUser.user.phone_number || "-",
						nick_name: currentUser.user.nick_name || "-",
						qualification: currentUser.user.qualification || "-",
						spesialization: currentUser.user.spesialization || "-",
						job_title: currentUser.user.job_title || "-",
						avatar: currentUser.user.avatar || null,
						title: currentUser.user.title || "-",
						created_at: currentUser.user.created_at,
						updated_at: currentUser.user.updated_at,
						subscription_status: currentUser.user.subscription_status,
						tos_agreement: currentUser.user.tos_agreement,
						privacy_policy_agreement: currentUser.user.privacy_policy_agreement,
						cookie_policy_agreement: currentUser.user.cookie_policy_agreement,
						profession: currentUser.user.profession,
						country: currentUser.user.country,
						status: currentUser.user.status,
						verified_at: currentUser.user.verified_at,
						app_version: currentUser.user.app_version,
						platform: currentUser.user.platform,
						pubnub_token_android: currentUser.user.pubnub_token_android,
						pubnub_token_ios: currentUser.user.pubnub_token_ios,
						referral_code: currentUser.user.referral_code,
						directory: currentUser.user.directory,
						patient_data_protection_policy:
							currentUser.user.patient_data_protection_policy,
					},
				];
			}
			optimisticGroup.pending_invitations = [];
			optimisticGroup.pending_teams = [];
			optimisticGroup.teams = [];
			optimisticGroup.last_message = null;

			const queryGroupsKey: [string, GetTeamsParamsSchema] = [
				QUERY_KEYS.GET_GROUPS,
				{
					directory: optimisticGroup.directory,
					scope: CONTACT_DIRECTORY_SCOPE.INTEREST_GROUPS,
				},
			];
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				queryGroupsKey,
				(queryDatas) => {
					if (!queryDatas) {
						return {
							pageParams: [1],
							pages: [
								{
									groups: [optimisticGroup as Group],
									meta: emptyMetaData,
								},
							],
						};
					}
					const newPages = queryDatas.pages[0];
					newPages.groups?.push(optimisticGroup as Group);
					return {
						pageParams: queryDatas.pageParams,
						pages: [newPages, ...(queryDatas.pages.slice(1) || [])],
					} as InfiniteData<GetTeamsResponse>;
				},
			);
			const queryAllKey: [string, GetTeamsParamsSchema] = [
				QUERY_KEYS.GET_GROUPS,
				{
					directory: optimisticGroup.directory,
					scope: CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
				},
			];
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				queryAllKey,
				(queryDatas) => {
					if (!queryDatas) {
						return {
							pageParams: [1],
							pages: [
								{
									groups: [optimisticGroup as Group],
									meta: emptyMetaData,
								},
							],
						};
					}
					const newPages = queryDatas.pages[0];
					newPages.teams?.push(optimisticGroup as Group);
					return {
						pageParams: queryDatas.pageParams,
						pages: [newPages, ...(queryDatas.pages.slice(1) || [])],
					} as InfiniteData<GetTeamsResponse>;
				},
			);

			return { optimisticGroup };
		},
		onSuccess: async (result, _, ctx) => {
			const optimisticGroupId = ctx.optimisticGroup.id;
			const directory = result.group.directory;
			const queryGroupsKey: [string, GetTeamsParamsSchema] = [
				QUERY_KEYS.GET_GROUPS,
				{
					directory,
					scope: CONTACT_DIRECTORY_SCOPE.INTEREST_GROUPS,
				},
			];
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				queryGroupsKey,
				(queryDatas) => {
					if (!queryDatas) {
						return {
							pageParams: [1],
							pages: [
								{
									groups: [result.group as Group],
									meta: emptyMetaData,
								},
							],
						};
					}
					const pages = queryDatas.pages.map((page) => {
						const groups = page.groups?.map((group) => {
							if (group.id === optimisticGroupId) {
								return result.group;
							}
							return group;
						});
						return { ...page, groups };
					});
					return {
						pageParams: queryDatas.pageParams,
						pages,
					};
				},
			);
			const queryAllKey: [string, GetTeamsParamsSchema] = [
				QUERY_KEYS.GET_GROUPS,
				{
					directory,
					scope: CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
				},
			];
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				queryAllKey,
				(queryDatas) => {
					if (!queryDatas) {
						return {
							pageParams: [1],
							pages: [
								{
									groups: [result.group as Group],
									meta: emptyMetaData,
								},
							],
						};
					}
					const pages = queryDatas.pages.map((page) => {
						const teams = page.teams?.map((group) => {
							if (group.id === optimisticGroupId) {
								return result.group;
							}
							return group;
						});
						return { ...page, teams };
					});
					return {
						pageParams: queryDatas.pageParams,
						pages,
					};
				},
			);
			const queryDetailKey: [string, number] = [
				QUERY_KEYS.GET_GROUP,
				result.group.id,
			];
			queryClient.setQueryData<GetGroupResponse>(
				queryDetailKey,
				pick(result, "group"),
			);
		},
		onError: async (_err, _variables, ctx) => {
			const optimisticGroupId = ctx?.optimisticGroup?.id;
			const directory = ctx?.optimisticGroup?.directory;
			const queryGroupsKey: [string, GetTeamsParamsSchema] = [
				QUERY_KEYS.GET_GROUPS,
				{
					directory,
					scope: CONTACT_DIRECTORY_SCOPE.INTEREST_GROUPS,
				},
			];
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				queryGroupsKey,
				(queryDatas) => {
					if (!queryDatas) {
						return {
							pageParams: [1],
							pages: [
								{
									groups: [],
									meta: defaultMetaData,
								},
							],
						};
					}
					const pages = queryDatas.pages.map((page) => {
						const groups = page.groups?.filter(
							(group) => group.id !== optimisticGroupId,
						);
						return { ...page, groups };
					});
					return {
						pageParams: queryDatas.pageParams,
						pages,
					};
				},
			);
			const queryAllKey: [string, GetTeamsParamsSchema] = [
				QUERY_KEYS.GET_GROUPS,
				{
					directory,
					scope: CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
				},
			];
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				queryAllKey,
				(queryDatas) => {
					if (!queryDatas) {
						return {
							pageParams: [1],
							pages: [
								{
									groups: [],
									meta: defaultMetaData,
								},
							],
						};
					}
					const pages = queryDatas.pages.map((page) => {
						const teams = page.teams?.filter(
							(group) => group.id !== optimisticGroupId,
						);
						return { ...page, teams };
					});
					return {
						pageParams: queryDatas.pageParams,
						pages,
					};
				},
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_GROUP_UPDATE], {
		retry: 2,
		mutationFn: (payload: UpdateGroupSchema) => groupService.update(payload),
		onMutate: async (updatedGroup) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_GROUP, updatedGroup.id],
			});
			const currentGroup = queryClient.getQueryData<GetGroupResponse>([
				QUERY_KEYS.GET_GROUP,
				updatedGroup.id,
			]);
			if (!currentGroup) return Promise.reject("Group not found");
			const avatar = isFiles(updatedGroup.avatar)
				? fileToString(updatedGroup.avatar).at(0)
				: updatedGroup.avatar;

			const optimisticGroup = {
				...currentGroup.group,
				...updatedGroup,
				avatar,
			};
			queryClient.setQueryData<GetGroupResponse>(
				[QUERY_KEYS.GET_GROUP, updatedGroup.id],
				{ group: optimisticGroup },
			);

			return { currentGroup, optimisticGroup };
		},
		onSuccess: (result, _, ctx) => {
			const optimisticGroup = ctx.optimisticGroup;
			queryClient.setQueryData<GetGroupResponse>(
				[QUERY_KEYS.GET_GROUP, optimisticGroup.id],
				{ ...result, group: result.group },
			);
		},
		onError: (_err, _variables, ctx) => {
			const optimisticGroup = ctx?.optimisticGroup;
			const currentGroup = ctx?.currentGroup;
			if (!optimisticGroup || !currentGroup) return;
			queryClient.setQueryData<GetGroupResponse>(
				[QUERY_KEYS.GET_GROUP, optimisticGroup.id],
				currentGroup,
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_GROUP_CLOSE], {
		mutationFn: (id: number) => groupService.close(id),
		retry: 2,
		onMutate: async (id) => {
			const currentGroup = queryClient.getQueryData<GetGroupResponse>([
				QUERY_KEYS.GET_GROUP,
				id,
			]);
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_GROUP, id],
			});
			if (!currentGroup) return Promise.reject("Group not found");
			const optimisticGroup: Group = {
				...currentGroup.group,
				status: "closed",
			};
			queryClient.setQueryData<GetGroupResponse>([QUERY_KEYS.GET_GROUP, id], {
				group: optimisticGroup,
			});

			return { optimisticGroup };
		},
		onError: (_err, _variables, ctx) => {
			const optimisticGroup = ctx?.optimisticGroup;
			if (!optimisticGroup) return;
			queryClient.setQueryData<GetGroupResponse>(
				[QUERY_KEYS.GET_GROUP, optimisticGroup.id],
				{
					group: {
						...optimisticGroup,
						status: "active",
					},
				},
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_GROUP_SYNC], {
		retry: 2,
		mutationFn: (params: SyncGroupParamsSchema) => groupService.sync(params),
		onSuccess: (res) => {
			queryClient.setQueryData<InfiniteData<GetGroupsResponse>>(
				[QUERY_KEYS.GET_GROUPS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							groups: applyDataChanges(p.groups || [], res.update, res.delete),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			useSyncStore.setState({
				groupSyncLastUnix: unix(),
			});
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_PREVIEW_URL], {
		retry: 2,
		mutationFn: (
			param: Parameters<typeof previewService.getLinkInformation>[0],
		) => previewService.getLinkInformation(param),
	});
};
